import React, { useReducer, useMemo, useState, useCallback, useEffect } from 'react'

import {
  Box,
  Display,
  JustifyContent,
  LoadingSpinner as LoadingComponent,
} from '@gousto-internal/citrus-react'
import { LayoutPageWrapper, Grid, Column } from 'goustouicomponents'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { useLocation } from 'react-use'

import {
  ResubscribeConfirmation,
  SubscriptionSettingsOverview,
  useResubOptimisationIntentHandler,
  useSubscriptionSettingsOverview,
} from '@features/resub-optimisation'

import { ValidationModal } from 'routes/Account/Subscription/ActiveSubscription/sections/YourSubscriptionDetails/ValidationModal'

import { ActiveSubscription } from './ActiveSubscription'
import { PausedSubscription } from './PausedSubscription'
import { SubscriptionPauseReasonOSR } from './SubscriptionPauseReasonOSR'
import { SubscriptionTitle } from './components/SubscriptionTitle'
import { ToastProvider } from './components/Toast'
import { SubscriptionContext } from './context'
import { SubscriptionReducer, initialSubscriptionsOptions } from './context/reducers'
import { getNumPortions } from './context/selectors/box'
import { getDeliveryFrequency } from './context/selectors/deliveries'
import { getIsSubscriptionActive, getIsSubscriptionLoaded } from './context/selectors/subscription'
import { useBoxPricesData } from './hooks/useBoxPricesData'
import { useCurrentUserData } from './hooks/useCurrentUserData'
import { useOrdersData } from './hooks/useOrdersData'
import { useSubscriptionData } from './hooks/useSubscriptionData'
import { useSubscriptionOptionsData } from './hooks/useSubscriptionOptionsData'

import css from './Subscription.css'

const propTypes = {
  accessToken: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
  startOnScreenRecoverySubscriptionFlow: PropTypes.func.isRequired,
  selectPauseReason: PropTypes.func.isRequired,
  defaultSubPauseReasonOSRFlow: PropTypes.bool,
  shouldClosePauseReasonsView: PropTypes.bool.isRequired,
  userLoadData: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
}

const defaultProps = {
  defaultSubPauseReasonOSRFlow: false,
}

const Subscription = ({
  accessToken,
  isMobile,
  startOnScreenRecoverySubscriptionFlow,
  defaultSubPauseReasonOSRFlow,
  shouldClosePauseReasonsView,
  selectPauseReason,
  userLoadData,
  userId,
}) => {
  const location = useLocation()
  const [showResubscribe, setShowResubscribe] = useState(
    new URLSearchParams(location.search).get('view') === 'resubscribe',
  )

  const [shouldRequestSubscriptionsV2, setShouldRequestSubscriptionsV2] = useState(false)
  const [shouldRequestSlots, setShouldRequestSlots] = useState(false)
  const [state, dispatch] = useReducer(SubscriptionReducer, {
    subscriptionOptions: initialSubscriptionsOptions,
  })
  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch])
  const isSubscriptionActive = getIsSubscriptionActive(state)
  const isSubscriptionLoaded = getIsSubscriptionLoaded(state)

  const { sections: resubOptimisationSections, isLoading: isResubOptimisationLoading } =
    useSubscriptionSettingsOverview(isSubscriptionLoaded, isSubscriptionActive)
  const { onAction: handleResubOptimisationAction } = useResubOptimisationIntentHandler(() =>
    setShowResubscribe(true),
  )

  const numPortions = getNumPortions(state)
  const deliveryFrequency = getDeliveryFrequency(state)

  const [isSubPauseReasonOSRFlowEnabled, enableSubPauseReasonOSRFlow] = useState(
    defaultSubPauseReasonOSRFlow,
  )

  useCurrentUserData(accessToken, dispatch)
  useBoxPricesData(accessToken, dispatch)
  useOrdersData(accessToken, dispatch)
  useSubscriptionData(
    accessToken,
    dispatch,
    {
      shouldRequest: shouldRequestSlots,
      setShouldRequest: setShouldRequestSlots,
    },
    {
      shouldRequest: shouldRequestSubscriptionsV2,
      setShouldRequest: setShouldRequestSubscriptionsV2,
    },
    state,
  )

  useSubscriptionOptionsData(dispatch, state)

  useEffect(() => {
    if (shouldClosePauseReasonsView) {
      enableSubPauseReasonOSRFlow(false)
    }
  }, [shouldClosePauseReasonsView])

  const handlePause = () => {
    enableSubPauseReasonOSRFlow(true)
  }

  const handleContinueToPauseButton = (reason) => {
    selectPauseReason(reason)
    startOnScreenRecoverySubscriptionFlow()
  }

  const handleBackButton = useCallback(() => {
    enableSubPauseReasonOSRFlow(false)
  }, [enableSubPauseReasonOSRFlow])

  if (!isSubscriptionLoaded) {
    return (
      <div className={css.loadingState}>
        <LoadingComponent />
      </div>
    )
  }

  const titleRow = (
    <Grid>
      <Column smallScreen={12} mediumScreen={12}>
        <SubscriptionTitle />
      </Column>
    </Grid>
  )

  return (
    <SubscriptionContext.Provider value={contextValue}>
      <Helmet title="Gousto Subscription | Manage Your Subscription Settings" />
      <ToastProvider>
        <ValidationModal accessToken={accessToken} />
        <LayoutPageWrapper size="medium" padding={false} testingSelector="subscriptionSettingsPage">
          {isSubPauseReasonOSRFlowEnabled ? (
            <SubscriptionPauseReasonOSR
              accessToken={accessToken}
              handleBackButton={handleBackButton}
              handlePauseSubmit={handleContinueToPauseButton}
            />
          ) : (
            <>
              {isSubscriptionActive ? (
                <>
                  {titleRow}
                  <ActiveSubscription
                    accessToken={accessToken}
                    isMobile={isMobile}
                    handlePause={handlePause}
                  />
                </>
              ) : isResubOptimisationLoading ? (
                <Box
                  display={Display.Flex}
                  justifyContent={JustifyContent.Center}
                  paddingV={4}
                  paddingH={4}
                >
                  <LoadingComponent />
                </Box>
              ) : resubOptimisationSections && showResubscribe ? (
                <ResubscribeConfirmation
                  userId={userId}
                  onResubscribed={userLoadData}
                  renderFallback={() => (
                    <>
                      {titleRow}
                      <PausedSubscription
                        accessToken={accessToken}
                        showResubscribe={showResubscribe}
                        setShowResubscribe={setShowResubscribe}
                        numPortions={numPortions}
                        frequency={deliveryFrequency}
                      />
                    </>
                  )}
                />
              ) : resubOptimisationSections ? (
                <SubscriptionSettingsOverview
                  sections={resubOptimisationSections}
                  onAction={handleResubOptimisationAction}
                />
              ) : (
                <>
                  {!showResubscribe && titleRow}
                  <PausedSubscription
                    accessToken={accessToken}
                    showResubscribe={showResubscribe}
                    setShowResubscribe={setShowResubscribe}
                    numPortions={numPortions}
                    frequency={deliveryFrequency}
                  />
                </>
              )}
            </>
          )}
        </LayoutPageWrapper>
      </ToastProvider>
    </SubscriptionContext.Provider>
  )
}

Subscription.propTypes = propTypes
Subscription.defaultProps = defaultProps

export { Subscription }
